import { isString } from 'lodash';
import { isValidElement } from 'react';

/**
 * Translates the react-table v7 column shape consumers still write into
 * TanStack v8 column definitions.
 *
 * @typedef {Object} V7Column
 * @property {string} [accessor]
 * @property {string} [id]
 * @property {string | React.ReactElement | React.ComponentType} [Header]
 * @property {React.ElementType} [Cell]
 * @property {number} [width]
 * @property {number} [minWidth]
 * @property {number} [maxWidth]
 * @property {boolean} [show]
 * @property {boolean} [hideHeader]
 * @property {boolean} [sortable]
 * @property {boolean} [sortDescFirst]
 */

/** @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`. */
export const adaptCellRenderer = CellRenderer => {
  const AdaptedCell = info => (
    <CellRenderer
      cell={{ value: info.getValue() }}
      row={{ original: info.row.original, index: info.row.index }}
      value={info.getValue()}
    />
  );
  // an anonymous component has an empty-string name, which ?? would not catch
  const name = CellRenderer.displayName || CellRenderer.name || 'Cell';
  AdaptedCell.displayName = `Adapted(${name})`;

  return AdaptedCell;
};

const adaptHeader = Header => {
  if (Header === undefined || isString(Header)) {
    return Header;
  }

  return isValidElement(Header) ? () => Header : () => <Header />;
};

const columnId = (column, index) =>
  column.id ?? column.accessor ?? `column-${index}`;

/**
 * @param {V7Column[]} columns
 *
 * @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`.
 */
export const toColumnDefs = columns =>
  columns.map((column, index) => {
    const { accessor, Header, Cell, width, minWidth, maxWidth } = column;

    return {
      id: columnId(column, index),
      ...(accessor === undefined ? {} : { accessorKey: accessor }),
      header: adaptHeader(Header),
      ...(Cell ? { cell: adaptCellRenderer(Cell) } : {}),
      ...(width === undefined ? {} : { size: width }),
      ...(minWidth === undefined ? {} : { minSize: minWidth }),
      ...(maxWidth === undefined ? {} : { maxSize: maxWidth }),
      enableSorting: column.sortable === true,
      ...(column.sortDescFirst === undefined
        ? {}
        : { sortDescFirst: column.sortDescFirst }),
    };
  });

/**
 * @param {V7Column[]} columns
 *
 * @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`.
 */
export const toColumnVisibility = columns =>
  columns.reduce(
    (visibility, column, index) =>
      column.show === undefined
        ? visibility
        : { ...visibility, [columnId(column, index)]: column.show },
    {},
  );

/**
 * The row props react-table v7 produced, which consumers still spread.
 *
 * @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`.
 */
export const V7_ROW_PROPS = {
  role: 'row',
  style: { display: 'flex', flex: '1 0 auto', minWidth: '0px' },
};

/**
 * Resolved from the original columns rather than column meta, which would need
 * a ColumnMeta module augmentation.
 *
 * @param {V7Column[]} columns
 *
 * @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`.
 */
export const toHiddenHeaderIds = columns =>
  new Set(
    columns
      .map((column, index) =>
        column.hideHeader === true ? columnId(column, index) : null,
      )
      .filter(Boolean),
  );
